/**
 * Which asset-model component fields are HIDDEN in the FFS Asset Model step.
 *
 * This is a DENYLIST scoped to the FFS workflow only: the shared component field
 * factories (`*AssetModelFormFields`, e.g. `PipeSectionAssetModelFormFields`) are
 * left untouched so the Asset Database and every other workflow still render the
 * full field set. FFS keeps only the inputs its FFS calcs consume plus
 * the fields needed to identify the component, and hides the rest.
 *
 * SCOPE — why this only affects component (Info-tab) fields. The predicate is
 * threaded, mirroring `fieldSupportsProvenance`, from `FfsAssetModelEditor` ->
 * `EditGeckoRichAssetModel` -> `FeatureSelectionPage` (Info tab only) ->
 * `EditableFeatureForm` -> `GeckoToggleForm`, where it folds into the per-field
 * visibility gate. A hidden field is NOT removed from the `FormField[]` — it is
 * only suppressed from render while staying registered in the form, so its value
 * round-trips untouched on save. (Dropping it from the array would blank it: the
 * JSON serializers, e.g. `generatePipeSection`, rebuild every scalar from the
 * form fields.) The `FormField[]` is built by `getFormFieldsForComponentType`,
 * so FEATURE fields (welds/supports/nozzles, rendered by `FeaturesTab`) and
 * asset-model-level fields never reach the gate — entries here only make sense
 * for component `dataKey`s.
 *
 * Keys are asset-model JSON `dataKey`s — the exact identifiers emitted by the
 * component factories (piping keys verified against `geckoPipeSection.ts` and the
 * `commonFields.ts` helpers). The match is exact and case-sensitive. NEVER key
 * off display labels.
 *
 * KEEP set (for reference — the fields NOT listed below): `name`, `radius`,
 * `wall_thickness`, `design_pressure`, `yield_strength`, `joint_efficiency`,
 * `pressure_coefficient`, `material`, `design_temp`,
 * `corrosion_on_inner_surface`.
 */

/** Geometry/orientation — grounds the 3D render, not an FFS calc input. */
const GEOMETRY_FIELDS = [
  'length',
  'orientation',
  'flow_direction',
  'inner_radius',
] as const

/** Where the component sits / how it is placed in the viz pane. */
const PLACEMENT_FIELDS = [
  'compass_orientation',
  'clock_bearing_rotation',
] as const

/** Bookkeeping metadata not consumed by the FFS calc. */
const METADATA_FIELDS = ['installation_date', 'stress_tolerance'] as const

/** Derived downstream — `tmin` is an OUTPUT of the Min Thickness step, not input. */
const COMPUTED_FIELDS = ['tmin'] as const

/**
 * Duplicates of a kept field. The allowable stress S is edited via
 * `yield_strength` (the material-stress picker writes there), so a separate
 * `allowable_stress` input only invites divergent values.
 */
const REDUNDANT_FIELDS = ['allowable_stress'] as const

/** Supplemental loads are assumed not to govern, so t_sl is not collected. */
const SUPPLEMENTAL_LOAD_FIELDS = ['tsl'] as const

const FFS_HIDDEN_ASSET_MODEL_FIELDS: ReadonlySet<string> = new Set([
  ...GEOMETRY_FIELDS,
  ...PLACEMENT_FIELDS,
  ...METADATA_FIELDS,
  ...COMPUTED_FIELDS,
  ...REDUNDANT_FIELDS,
  ...SUPPLEMENTAL_LOAD_FIELDS,
])

/** True when this asset-model component field is hidden in the FFS step. */
export const isFieldHiddenInFfs = (dataKey: string): boolean =>
  FFS_HIDDEN_ASSET_MODEL_FIELDS.has(dataKey)
